// FicheFormation.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import InfoFormation from './InfoFormation';
import CorrespondanceFormation from './CorrespondanceFormation';
import styled from 'styled-components';

import {  
  ButtonsContainer, 
  CityContainer, 
  ButtonIcon, 
  CityText, 
  ActionButton, 
  ButtonText, 
  ButtonIconContainer,
  Container, 
  HeaderWrapper, 
  HeaderSvg, 
  HeaderContent, 
  HeaderTopRow, 
  ReturnButton, 
  Subtitle, 
  IconsContainer, 
  ShareIcon, 
  LikeIcon, 
  HeaderBottomRow, 
  FormationName, 
  SchoolName, 
  FloatingHeart,
  TabsContainer, 
  TabButton,
  ScrollableContent,
  StickyHeaderContainer,
} from './styles';


import localisation_icon from '../../assets/localisation_icon.png';
import parcoursup_icon from '../../assets/out.png';
import school_icon from '../../assets/out.png';
import MatchIcon from '../../assets/correspondance_icon.png';
import InfoIcon from '../../assets/infos_icon.png';
import trophyImgSrc from '../../assets/meo_trophy_v2.png'; 

import apiCall from '../../utils/api';
import { FaHeart, FaRegHeart } from 'react-icons/fa';
import { MdOutlineShare } from 'react-icons/md';
import { IoIosArrowBack } from 'react-icons/io';

import useDeviceType from '../useDeviceType';

// Imports for Formacode data loading
import { parseFormacodeData } from '../../utils/formaCodeUtils'; 
// domainIconsFormacodesSousGrandDomaines is imported from CorrespondanceFormation's domainIconMap.
// Assuming domainIconMap.js is in FicheFormation/CorrespondanceFormation/domainIconMap.js
// and this index.js is in FicheFormation/index.js
import { domainIconsFormacodesSousGrandDomaines } from '../Onboarding/domainIconMap';


const Toast = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 10px 20px;
  border-radius: 20px;
  font-family: 'Gabarito', sans-serif;
  font-size: 14px;
  z-index: 3000;
  opacity: ${props => (props.visible ? '1' : '0')};
  transition: opacity 0.3s ease;
`;

const FicheFormation = () => {
  const { isMobile } = useDeviceType();
  const navigate = useNavigate();
  const location = useLocation();
  const [formation, setFormation] = useState({});
  const [posNeg, setPosNeg] = useState({});
  const [loading, setLoading] = useState(true); // General loading state
  const [error, setError] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [animateLike, setAnimateLike] = useState(false);
  const [animateShare, setAnimateShare] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState(""); // Ajout pour message dynamique
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const [isFormationNameTruncated, setIsFormationNameTruncated] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const idFormation = searchParams.get('id');
  const tabParam = searchParams.get('tab'); 
  const [activeTab, setActiveTab] = useState(tabParam === 'infos' ? 'infos' : 'correspondance'); //le tab par défaut est 'correspondance'
  const lastClickTimeRef = useRef(0);
  const [clickPosition, setClickPosition] = useState(null);
  const DOUBLE_CLICK_THRESHOLD = 300;
  const formationNameRef = useRef(null);
  const textContainerRef = useRef(null);
  const headerTopRowRef = useRef(null);
  const buttonsContainerRef = useRef(null);

  // States for Formacode data
  const [formacodeData, setFormacodeData] = useState({ formacodes: [], hierarchy: [] });
  const [hierarchyCodeToIconMap, setHierarchyCodeToIconMap] = useState(new Map());
  const [isFormacodeDataLoading, setIsFormacodeDataLoading] = useState(true); // Specific loading for formacodes

  useEffect(() => {
    if (location.state?.from) {
      sessionStorage.setItem('ficheFormationBackLink', location.state.from);
    }
  }, [location.state?.from]);

  const getHierarchyDistance = (code1, code2) => {
    if (!code1 || !code2) return Infinity;
    code1 = String(code1);
    code2 = String(code2);
    if (code1 === code2) return 0;
    
    // Parent/child relationship is closer
    if (code1.startsWith(code2)) return (code1.length - code2.length);
    if (code2.startsWith(code1)) return (code2.length - code1.length);

    // Sibling relationship
    let i = 0;
    while (i < code1.length && i < code2.length && code1[i] === code2[i]) {
        i++;
    }
    // Add a penalty to distinguish from parent/child.
    return (code1.length - i) + (code2.length - i) + 2;
  };

  const sortedDomainsForCorrespondance = React.useMemo(() => {
    if (!formation || !posNeg || !posNeg.domaines) {
        return [];
    }

    const studentPrincipalDomains = posNeg.domaines.principaux || [];
    const studentSecondaryDomains = posNeg.domaines.secondaires || [];

    const formationPrincipalDomains = formation.domaines_enseignés_principaux || [];
    const formationSecondaryDomains = formation.domaines_enseignés_secondaires || [];

    const allFormationDomains = [
        ...formationPrincipalDomains.map(d => ({ domainCode: d, type: 'principal' })),
        ...formationSecondaryDomains.map(d => ({ domainCode: d, type: 'secondaire' }))
    ];

    const domainsWithDistances = allFormationDomains.map(d => {
        const distToPrincipal = studentPrincipalDomains.length > 0
            ? Math.min(...studentPrincipalDomains.map(sd => getHierarchyDistance(d.domainCode, sd)))
            : Infinity;
        
        const distToSecondary = studentSecondaryDomains.length > 0
            ? Math.min(...studentSecondaryDomains.map(sd => getHierarchyDistance(d.domainCode, sd)))
            : Infinity;

        return { ...d, distToPrincipal, distToSecondary };
    });

    domainsWithDistances.sort((a, b) => {
        if (a.distToPrincipal !== b.distToPrincipal) {
            return a.distToPrincipal - b.distToPrincipal;
        }
        if (a.distToSecondary !== b.distToSecondary) {
            return a.distToSecondary - b.distToSecondary;
        }
        // Tie-breaker: formation's principal domains first
        if (a.type === 'principal' && b.type !== 'principal') return -1;
        if (a.type !== 'principal' && b.type === 'principal') return 1;
        return 0;
    });

    return domainsWithDistances;
  }, [formation, posNeg]);


  useEffect(() => {
    const textContainer = textContainerRef.current;
    const topRow = headerTopRowRef.current;
    const buttons = buttonsContainerRef.current;
    
    if (textContainer && topRow && buttons && (formation.nom_formation || formation.nom_ecole)) {
      const calculateHeight = () => {
        const topRowHeight = topRow.offsetHeight;
        const textBlockHeight = textContainer.scrollHeight;
        const buttonsHeight = buttons.offsetHeight;
        
        // Summing up the parts with some padding/margin considerations
        const calculatedHeight = topRowHeight + textBlockHeight + buttonsHeight + (isMobile ? 15 : 20); // Adjust vertical spacing as needed
      };

      // Calculate height on first render
      calculateHeight();

      // Recalculate after fonts are loaded to correct for any reflow
      document.fonts.ready.then(calculateHeight);
    }
  }, [formation.nom_formation, formation.nom_ecole, isMobile]);

  useEffect(() => {
    const checkTruncation = () => {
      if (formationNameRef.current) {
        const { scrollHeight, clientHeight } = formationNameRef.current;
        // Using a small tolerance for floating point inaccuracies
        setIsFormationNameTruncated(scrollHeight > clientHeight + 1);
      }
    };
  
    // Check after a short delay to allow for rendering and transitions.
    const timer = setTimeout(checkTruncation, 300);
    window.addEventListener('resize', checkTruncation);
  
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [formation.nom_formation, isHeaderExpanded]);

  // Load Formacode Data
  useEffect(() => {
    const loadFormacodeData = async () => {
      setIsFormacodeDataLoading(true);
      try {
        const response = await fetch('/formacodes_recoded_optimized.csv');
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const csvData = await response.text();
        if (!csvData || csvData.trim() === "") throw new Error("CSV vide");
        
        const parsed = parseFormacodeData(csvData);
        setFormacodeData(parsed);
        
        const newHierarchyMap = new Map();
        Object.entries(domainIconsFormacodesSousGrandDomaines).forEach(([domainName, icon]) => {
          const formacodeEntry = parsed.formacodes.find(
            fc => fc.descriptorRich === domainName && 
                  fc.hierarchyCode && 
                  fc.hierarchyCode.length === 4
          );
          if (formacodeEntry && formacodeEntry.hierarchyCode) {
            newHierarchyMap.set(formacodeEntry.hierarchyCode, icon);
          }
        });
        setHierarchyCodeToIconMap(newHierarchyMap);
      } catch (error) {
        console.error('Erreur chargement formacode:', error);
        // Potentially set an error state for formacode loading
      } finally {
        setIsFormacodeDataLoading(false);
      }
    };
    loadFormacodeData();
  }, []);


  useEffect(() => {
    const trackCardVisit = async () => {
      if (!formation || !formation.id) {
        console.warn("Cannot track visit: formation data or id missing.");
        return;
      }
      try {
        await apiCall(
          `${process.env.REACT_APP_BACKEND_URL}/auth/track-card-visit/`,
          'POST',
          { formation_id: formation.id, code_parcoursup: formation.code_parcoursup, interaction_type: 'view' }
        );
      } catch (error) {
        console.error('Error tracking card visit:', error);
      }
    };

    if (idFormation && !loading && formation && Object.keys(formation).length > 0) {
      trackCardVisit();
    }
  }, [idFormation, loading, formation]);

  const trackInteraction = async (type) => {
    if (!formation || !formation.id) {
      console.error(`Cannot track ${type} interaction: formation data or id missing.`);
      return;
    }
    try {
      await apiCall(
        `${process.env.REACT_APP_BACKEND_URL}/auth/track-card-visit/`,
        'POST',
        { formation_id: formation.id, code_parcoursup: formation.code_parcoursup, interaction_type: type }
      );
      console.log(`${type} interaction tracked for card ${formation.code_parcoursup || formation.id}`);
    } catch (error) {
      console.error(`Error tracking ${type} interaction:`, error);
    }
  };

  const handleHeaderClick = (event) => {
    if (event.target.closest('[data-exclude="true"]')) return;

    // If the name is truncated, or if the header is already expanded, a single click should toggle expansion.
    if (isFormationNameTruncated || isHeaderExpanded) {
      setIsHeaderExpanded(!isHeaderExpanded);
      return; // Prevent double-click-to-like.
    }

    const now = Date.now();
    if (now - lastClickTimeRef.current < DOUBLE_CLICK_THRESHOLD) {
      const rect = event.currentTarget.getBoundingClientRect();
      setClickPosition({ x: event.clientX - rect.left, y: event.clientY - rect.top, key: Date.now() });
      if (!isFavorite) {
        setAnimateLike(true);
        handleLike();
        setTimeout(() => setAnimateLike(false), 500);
      }
    }
    lastClickTimeRef.current = now;
  };

  useEffect(() => {
    const need_update_ranking_formation = JSON.parse(localStorage.getItem("need_update_ranking_formation"));
    
    const fetchFormationData = async () => {
      setLoading(true); // Start general loading
      try {
        // Fetch PosNeg
        var posNegData = null;
        if (!need_update_ranking_formation) {
          posNegData = JSON.parse(localStorage.getItem("pos_neg"));
        }
        if (!posNegData || !posNegData.positive || !posNegData.negative || 
            (Array.isArray(posNegData.positive) && posNegData.positive.length === 0 && 
             Array.isArray(posNegData.negative) && posNegData.negative.length === 0)) {
          const url = `${process.env.REACT_APP_BACKEND_URL}/quiz/pos-neg/`;
          posNegData = await apiCall(url, 'GET');
          localStorage.setItem("pos_neg", JSON.stringify(posNegData));
        }
        setPosNeg(posNegData);

        // Fetch Formation
        var rankingData = null;
        if (!need_update_ranking_formation) {
          const data = JSON.parse(localStorage.getItem("ranking_formation"));
          rankingData = data ? Object.values(data).find(r => String(r.id) === String(idFormation)) : null;
        }
        if (!rankingData) {
          const url = `${process.env.REACT_APP_BACKEND_URL}/quiz/ficheFormation/?id=${idFormation}`;
          rankingData = await apiCall(url, 'GET');
        }
        setFormation(rankingData);
        setIsFavorite(rankingData.is_favorite || false);
        
      } catch (err) {
        console.error('Erreur fetching formation or posneg:', err);
        setError(true);
      } finally {
        setLoading(false); // End general loading
      }
    };

    if (idFormation) {
      fetchFormationData();
    } else {
      setError(true);
      setLoading(false);
      setIsFormacodeDataLoading(false); // Also stop formacode loading if no ID
    }
  }, [idFormation]);

  const handleLike = async () => {
    const newFavoriteStatus = !isFavorite;
    if (!formation || !formation.code_parcoursup) {
      console.error("Cannot update favorite status: code_parcoursup is missing.");
      return;
    }
    try {
      if (newFavoriteStatus) {
        await apiCall(
          `${process.env.REACT_APP_BACKEND_URL}/auth/ajouter_favoris_formation/`, 'POST',
          { code_parcoursup: formation.code_parcoursup, matchPercentage: formation?.score_formations || 0 }
        );
        trackInteraction('favorite_add');
      } else {
        await apiCall(
          `${process.env.REACT_APP_BACKEND_URL}/auth/supprimer_favoris_formation/${formation.id}/`,
          'DELETE'
        );
        trackInteraction('favorite_remove');
      }
      setIsFavorite(newFavoriteStatus);
      setFormation(prev => ({ ...prev, is_favorite: newFavoriteStatus }));

      const cachedRankings = JSON.parse(localStorage.getItem("ranking_formation"));
      if (cachedRankings) {
        let foundAndUpdated = false;
        Object.keys(cachedRankings).forEach(key => {
          if (String(cachedRankings[key].id) === String(idFormation) || cachedRankings[key].code_parcoursup === formation.code_parcoursup) {
            cachedRankings[key].is_favorite = newFavoriteStatus;
            foundAndUpdated = true;
          }
        });
        if (foundAndUpdated) {
          localStorage.setItem("ranking_formation", JSON.stringify(cachedRankings));
        }
      }
    } catch (error) {
      console.error('Error updating favorite:', error);
      // Optional: Revert state on error? Decide based on desired UX
    }
  };
  
  /**
   * Génère une image PNG (data URL) représentant le pourcentage de compatibilité, façon "canvas".
  matchPercentage - Le pourcentage de compatibilité (0-100).
  number La taille de l'image (largeur/hauteur en px).
  retourne une data URL de l'image PNG.
  */
 async function generateCompatibilityImage(matchPercentage, schoolName, formationName, size = 500) {
  /* TODO
  - interface système
  - toast 
  - 
  */
   const canvas = document.createElement('canvas');
   const ctx = canvas.getContext('2d');
   const scale = window.devicePixelRatio || 1;
   canvas.width = size * scale;
   canvas.height = size * scale;
   ctx.scale(scale, scale);
   const center = size / 2;
  
  // border radius
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(25, 0);
  ctx.lineTo(size - 25, 0);
  ctx.quadraticCurveTo(size, 0, size, 25);
  ctx.lineTo(size, size - 25);
  ctx.quadraticCurveTo(size, size, size - 25, size);
  ctx.lineTo(25, size);
  ctx.quadraticCurveTo(0, size, 0, size - 25);
  ctx.lineTo(0, 25);
  ctx.quadraticCurveTo(0, 0, 25, 0);
  ctx.closePath();
  ctx.clip();

    // Fond de l'image
    // const gradient = ctx.createLinearGradient(0, 0, size, size);
    // gradient.addColorStop(0, '#ff2768');
    // gradient.addColorStop(1, '#f15dd7');
    // ctx.fillStyle = gradient;
    ctx.fillStyle = '#ff2768';
    ctx.fillRect(0, 0, size, size);

    // image de meo
    const trophyImg = new window.Image();
    trophyImg.src = trophyImgSrc;
    await new Promise((resolve) => {
      trophyImg.onload = resolve;
      trophyImg.onerror = resolve;
    });
    const trophySize = size * 0.75;
    ctx.drawImage(
      trophyImg,
      center - trophySize / 2,
      center - trophySize * 0.85,
      trophySize,
      trophySize
    );


    
    // Texte du pourcentage
    ctx.font = `bold ${Math.round(size * 0.35)}px Gabarito`;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = Math.round(10);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.strokeText(`${Math.round(matchPercentage)}%`, center, center);
    ctx.fillStyle = '#ff2768';
    ctx.fillText(`${Math.round(matchPercentage)}%`, center, center);


    // Légende
    const font_size = size * 0.05
    ctx.font = `bold ${Math.round(font_size/1.5)}px Gabarito`;
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(`Mon profil est compatible avec cette formation : `, center, center + size * 0.19);

    //couper au premier espace après 24 caractères
    ctx.font = `bold ${Math.round(font_size)}px Gabarito`;
    const words = formationName.split(' ');
    let line = '';
    let lines = [];
    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + ' ';
      const metrics = ctx.measureText(testLine);
      const testWidth = metrics.width;
      if (testWidth > size * 0.8 && i > 0) {
        lines.push(line);
        line = words[i] + ' ';
      } else {
        line = testLine;
      }
    }
    lines.push(line);
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], center, center + size * 0.19 + font_size * (i + 1));
    }

    const schoolWords = (schoolName || '').split(' ');
    let schoolLine = '';
    let schoolLines = [];
    for (let i = 0; i < schoolWords.length; i++) {
      const testLine = schoolLine + schoolWords[i] + ' ';
      const metrics = ctx.measureText(testLine);
      const testWidth = metrics.width;
      if (testWidth > size * 0.8 && i > 0) {
        schoolLines.push(schoolLine);
        schoolLine = schoolWords[i] + ' ';
      } else {
        schoolLine = testLine;
      }
    }
    schoolLines.push(schoolLine);
    for (let i = 0; i < schoolLines.length; i++) {
      ctx.fillText(
        schoolLines[i],
        center,
        center + size * 0.19 + font_size * (lines.length + i + 1)
      );
    }

    // Légende école

    ctx.font = `bold ${Math.round(font_size/2)}px Gabarito`;
    ctx.textAlign = 'left';
    ctx.fillText("Meoria", 5, size - font_size - 5)


    return canvas.toDataURL('image/png');
  }
  
const handleShare = async () => {
  const urlWithTab = `${window.location.origin}${location.pathname}?id=${idFormation}&tab=infos`;

  // Génère la data URL de l'image
  const dataUrl = await generateCompatibilityImage(
    formation?.score_formations || 0,
    formation?.nom_ecole,
    formation.nom_formation,
    300
  );

  // Texte à partager
  const shareText = `Découvrez cette formation sur Meoria !\n${urlWithTab}`;

  // Web Share API avec image si possible
  if (navigator.canShare && navigator.canShare({ files: [] })) {
    try {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], "formation-meoria.png", { type: "image/png" });

      await navigator.share({
        title: "Formation Meoria",
        text: shareText,
        files: [file],
        url: urlWithTab,
      });
      return;
    } catch (err) {
      // fallback
    }
  } else if (navigator.share) {
    // Web Share API sans image
    try {
      await navigator.share({
        title: "Formation Meoria",
        text: shareText,
        url: urlWithTab,
      });
      return;
    } catch (err) {
      // fallback
    }
  }

  // Fallback : copie l'image dans le presse-papiers si possible, sinon le lien
  try {
    // Tentative de copier l'image (Clipboard API moderne)
    if (navigator.clipboard && window.ClipboardItem) {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      await navigator.clipboard.write([
        new window.ClipboardItem({ [blob.type]: blob })
      ]);
      setToastMessage("image copiée");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
      return;
    }
  } catch (err) {
    // fallback
  }
  // Sinon, copie le lien
  try {
    await navigator.clipboard.writeText(urlWithTab);
    setToastMessage("lien copié");
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  } catch (err) {
    alert("Impossible de partager ou de copier le lien.");
  }
};

  const handleBack = () => {
    const backLink = sessionStorage.getItem('ficheFormationBackLink');
    if (backLink) {
      navigate(backLink);
    } else {
      navigate('/explore');
    }
  };

  // Combined loading state
  if (loading || isFormacodeDataLoading) return <div style={{textAlign: 'center', marginTop: '50px', fontSize: '18px'}}>Chargement des informations de la formation...</div>;
  if (error) return <div style={{textAlign: 'center', marginTop: '50px', fontSize: '18px', color: 'red'}}>Erreur de chargement des données. Veuillez réessayer.</div>;


  return (
    <Container isMobile={isMobile}>
      <StickyHeaderContainer>
        <HeaderWrapper isMobile={isMobile} onClick={handleHeaderClick}>
          {clickPosition && (
            <FloatingHeart
              key={clickPosition.key}
              style={{ left: `${clickPosition.x}px`, top: `${clickPosition.y}px` }}
              onAnimationEnd={() => setClickPosition(null)}
            ><FaHeart /></FloatingHeart>
          )}
          <HeaderSvg preserveAspectRatio="none" viewBox="0 0 379 168">
            <path
              d="M0 25C0 11.1929 11.1929 0 25 0H354C367.807 0 379 11.1929 379 25V143C379 156.807 367.807 168 354 168H25C11.1929 168 0 156.807 0 143V25Z"
              fill="url(#paint0_linear_287_452)"
            />
            <defs>
              <linearGradient id="paint0_linear_287_452" x1="0" y1="84" x2="379" y2="84" gradientUnits="userSpaceOnUse">
                <stop stopColor="#CC00FF" /><stop offset="1" stopColor="#0F04E3" />
              </linearGradient>
            </defs>
          </HeaderSvg>
          <HeaderContent>
            <HeaderTopRow ref={headerTopRowRef}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <ReturnButton onClick={(e) => { e.stopPropagation(); handleBack(); }}><IoIosArrowBack /></ReturnButton>
                <Subtitle>Formation</Subtitle>
              </div>
              <IconsContainer>
                <ShareIcon $animate={animateShare} onClick={(e) => { setAnimateShare(true); e.stopPropagation(); handleShare(); setTimeout(() => setAnimateShare(false), 300); }}>
                  <MdOutlineShare />
                </ShareIcon>
                <LikeIcon data-exclude="true" $animate={animateLike} $isFavorite={isFavorite} onClick={(e) => { e.stopPropagation(); handleLike(); setAnimateLike(true); setTimeout(() => setAnimateLike(false), 500);}}>
                  {isFavorite ? <FaHeart /> : <FaRegHeart />}
                </LikeIcon>
              </IconsContainer>
            </HeaderTopRow>
            <HeaderBottomRow>
              <div style={{ display: 'flex', flexDirection: 'column' }} ref={textContainerRef}>
              <FormationName ref={formationNameRef} title={formation.nom_formation} isExpanded={isHeaderExpanded}>
                  {formation.nom_formation}
                </FormationName>
                <SchoolName title={formation.nom_ecole}>
                  {formation.nom_ecole ? formation.nom_ecole.split('(')[0].trim() : 'École non spécifiée'}
                </SchoolName>
              </div>
            </HeaderBottomRow>
            <ButtonsContainer ref={buttonsContainerRef}>
              <CityContainer>
                <ButtonIcon src={localisation_icon} alt="Location" />
                <CityText>{formation.ville ? formation.ville.slice(0, 20) : 'Ville non spécifiée'}</CityText>
              </CityContainer>
              {formation.site && (
              <ActionButton href={formation.site} target="_blank" onClick={() => trackInteraction('site')}>
                <ButtonText>Site de l'école</ButtonText>
                <ButtonIconContainer><ButtonIcon src={school_icon} alt="School" /></ButtonIconContainer>
              </ActionButton>)}
              {formation.link && (
              <ActionButton href={formation.link} target="_blank" onClick={() => trackInteraction('parcoursup')}>
                <ButtonText>Parcoursup</ButtonText>
                <ButtonIconContainer><ButtonIcon src={parcoursup_icon} alt="Parcoursup" /></ButtonIconContainer>
              </ActionButton>)}
            </ButtonsContainer>
          </HeaderContent>
        </HeaderWrapper>

        <TabsContainer>
          <TabButton active={activeTab === 'correspondance'} onClick={() => setActiveTab('correspondance')}>
            <img src={MatchIcon} alt="Match icon" style={{ width: '18px', height: '18px' }}/>
            <span>Match</span>
          </TabButton>
          <TabButton active={activeTab === 'infos'} onClick={() => {
            setActiveTab('infos');
            trackInteraction('infos_click');
          }}>
            <img src={InfoIcon} alt="Infos icon" style={{ width: '18px', height: '18px' }} />
            <span>Infos</span>
          </TabButton>
        </TabsContainer>
      </StickyHeaderContainer>
      {/* <RectangleDiv headerHeight={headerHeight} /> */}

      <ScrollableContent>
        {activeTab === 'correspondance' ? (
          <CorrespondanceFormation 
            formation={formation} 
            posNeg={posNeg} 
            formacodeData={formacodeData}
            hierarchyCodeToIconMap={hierarchyCodeToIconMap} 
            sortedDomains={sortedDomainsForCorrespondance}
          />
        ) : (
          <InfoFormation 
            formation={formation} 
            formacodeData={formacodeData}
            hierarchyCodeToIconMap={hierarchyCodeToIconMap}
            trackInteraction={trackInteraction}
          />
        )}
      </ScrollableContent>
        {/* ✅ Ajout du toast ici */}
      {showToast && (
        <Toast visible={showToast}>{toastMessage}</Toast>
      )}
    </Container>
  );
};

export default FicheFormation;